import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { StatusValue } from '../StatusCheckInDialog/CheckInFormContent';

export function useFeedTaskCheckIn(
  onCheckInSubmit?: (statusValue: StatusValue, comment?: string) => void
) {
  const { t } = useTranslation();
  const [expandedCheckInTaskId, setExpandedCheckInTaskId] = useState<string | null>(null);
  const [isCheckInSubmitting, setIsCheckInSubmitting] = useState(false);
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkInSuccessTaskId, setCheckInSuccessTaskId] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleExpandChange = useCallback((taskId: string, expanded: boolean) => {
    setCheckInError(null);
    setExpandedCheckInTaskId(expanded ? taskId : null);
  }, []);

  const handleSubmit = useCallback(
    async (statusValue: StatusValue, comment?: string) => {
      setIsCheckInSubmitting(true);
      setCheckInError(null);
      try {
        onCheckInSubmit?.(statusValue, comment);
        setCheckInSuccessTaskId(expandedCheckInTaskId);
        setExpandedCheckInTaskId(null);
        successTimerRef.current = setTimeout(() => setCheckInSuccessTaskId(null), 2000);
      } catch {
        setCheckInError(t('checkIn.inline.errorMessage'));
      } finally {
        setIsCheckInSubmitting(false);
      }
    },
    [expandedCheckInTaskId, onCheckInSubmit, t]
  );

  return {
    expandedCheckInTaskId,
    isCheckInSubmitting,
    checkInError,
    checkInSuccessTaskId,
    handleExpandChange,
    handleSubmit,
  };
}
